import { useCallback, useRef } from 'react';

import { TEvFiltersPanelValues } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { mapPanelValuesToFilters } from 'src/common/helpers/FiltersPanelHelper';

interface IUseIncludeZeroIssueToggleParams {
	tableId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	showIncludeZeroIssueToggle: boolean;
}

interface IUseIncludeZeroIssueToggleResult {
	includeScansWithoutIssues: boolean;
	handleApply: (appliedFilters: TEvFiltersPanelValues) => void;
	handleReset: (appliedFilters: TEvFiltersPanelValues) => void;
	handleToggleChange: (value: boolean) => void;
}

export const useIncludeZeroIssueToggle = ({
	tableId,
	filters,
	onFilterChange,
	showIncludeZeroIssueToggle
}: IUseIncludeZeroIssueToggleParams): IUseIncludeZeroIssueToggleResult => {
	const filtersRef = useRef(filters);
	filtersRef.current = filters;

	const includeScansWithoutIssues = filters.includeScansWithoutIssues ?? false;

	const handleApply = useCallback(
		(appliedFilters: TEvFiltersPanelValues): void => {
			onFilterChange(tableId, {
				...mapPanelValuesToFilters(appliedFilters),
				...(showIncludeZeroIssueToggle
					? { includeScansWithoutIssues: filtersRef.current.includeScansWithoutIssues ?? false }
					: {})
			});
		},
		[onFilterChange, showIncludeZeroIssueToggle, tableId]
	);

	const handleReset = useCallback(
		(appliedFilters: TEvFiltersPanelValues): void => {
			onFilterChange(tableId, {
				...mapPanelValuesToFilters(appliedFilters),
				...(showIncludeZeroIssueToggle ? { includeScansWithoutIssues: false } : {})
			});
		},
		[onFilterChange, showIncludeZeroIssueToggle, tableId]
	);

	const handleToggleChange = useCallback(
		(value: boolean): void => {
			onFilterChange(tableId, {
				...filtersRef.current,
				includeScansWithoutIssues: value
			});
		},
		[onFilterChange, tableId]
	);

	return { includeScansWithoutIssues, handleApply, handleReset, handleToggleChange };
};
